// API Client for Iron Forums

import axios from 'axios';
import type {
  AuthTokens,
  User,
  UserProfile,
  UserProfileListItem,
  MembershipRegistration,
  PaymentStatusResponse,
  BillingSummary,
  MemberReceipt,
  ProfileSearchFilters,
  OnboardingResponse,
  OnboardingGreeting,
  Snapshot,
  SnapshotCategory,
  Commitment,
  CommitmentCheckIn,
  Thread,
  Message,
  Teaching,
  TeachingCreate,
  TeachingVisibility,
  BulkUploadResult,
  JarvisResponse,
  JarvisConversation,
  JarvisMemory,
  SnapshotHistoryEntry,
  SnapshotAggregates,
  CurrentPeriodResponse,
  AreaHealthDefinition,
  SnapshotGroup,
  SnapshotGroupListItem,
  MonthlySnapshotGroup,
  MonthlySnapshotGroupDetail,
  AccountabilityGroup,
  AccountabilityGroupDetail,
  AccountabilityInvitation,
  AccountabilityPendingInvitations,
  GroupInvitation,
  GroupActivity,
  InviteTokenInfo,
  ChatMessage,
  SnapshotChatResponse,
  SnapshotSummaryResponse,
  UserListItem,
  PrayerRequest,
  PrayerJournalEntry,
  SnapshotPrayer,
  UnifiedPrayer,
  StructuredPrayerSummary,
  Forum,
  ForumListItem,
  ForumDetail,
  ForumInvitation,
  ForumType,
  MeetingDay,
  MeetingWeek,
  GeocodeSuggestion,
  MemberTrendsResponse,
  FacilitatorDashboardResponse,
  Broadcast,
  BroadcastAudience,
  ForumHealthMetric,
  ForumHealthRadarResponse,
  ForumHealthTrendResponse,
  AdminActivityResponse,
  OrgHealthRadarResponse,
  OrgHealthTrendResponse,
  MemberCategoryTrendResponse,
  ForumAttendanceResponse,
  ForumAttendanceToggleResponse,
  ForumHistoryResponse,
  GrowGoalPrompt,
  ProfileRequirement,
  ProfileRequirementAdmin,
  ProfileRequirementAnswerRow,
  ActiveGrowGoalPromptResponse,
  Sponsor,
  CurrentSponsorResponse,
  AdminDashboardResponse,
  AdminInsights,
  ExpertiseTag,
  ProfileVisibilitySettings,
  ForumSchedule,
  UpcomingEvent,
  ForumRoster,
  CalendarConnection,
  CalendarProvider,
  MeetingMode,
  MeetingRequest,
  PaymentTrackingRow,
  PaymentReminderFields,
  FacilitatorMemberDetail,
  FacilitatorNote,
  AdminMemberRow,
  AdminMembersQuery,
  AdminMemberExportField,
  InviteRowPayload,
  BatchInviteResponse,
  ScheduledInvite,
  ScheduledInviteUpdate,
  BulkScheduledInviteResponse,
  FeedbackItem,
  MyTicket,
  TicketAccessPerson,
  CodingApproval,
  TwoFactorPrompt,
  TwoFactorStatus,
  TicketDetail,
  TicketSummary,
  TicketPerson,
  TicketDecision,
  MemberEmailPreview,
  DecisionTicket,
  WhatsNewDigest,
  WhatsNewDraft,
  WhatsNewFeed,
  WhatsNewItem,
  Note,
  NoteWrite,
  LinkableMeetings,
  MemberBillingInfo,
  BillingOverviewRow,
  ScheduledCharge,
} from '../types';
import type { AppVersionInfo } from '../lib/appVersion';

import { Capacitor } from '@capacitor/core';

function getApiBase(): string {
  const envUrl = import.meta.env.VITE_API_URL;

  if (Capacitor.isNativePlatform()) {
    if (envUrl) return envUrl;
    const host = Capacitor.getPlatform() === 'android' ? '10.0.2.2' : 'localhost';
    return `http://${host}:8000/api`;
  }

  return envUrl || '/api';
}

export const API_BASE = getApiBase();

function triggerBrowserDownload(url: string, filename?: string) {
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function toApiRelativePath(url: string): string | null {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url) && !url.startsWith('/')) {
    return url.replace(/^\//, '');
  }
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.pathname.startsWith('/api/')) return null;
    return `${parsed.pathname.slice('/api/'.length)}${parsed.search}`;
  } catch {
    return null;
  }
}

// Create axios instance (timeout so login/requests fail fast instead of hanging when backend is down/slow)
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // TODO: Migrate token storage from localStorage to httpOnly cookies for XSS protection
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh token mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        processQueue(new Error('No refresh token'), null);
        isRefreshing = false;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE}/token/refresh/`, {
          refresh: refreshToken,
        });
        const newAccessToken = response.data.access;
        localStorage.setItem('access_token', newAccessToken);
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Only a rejected refresh token ends the session. A rate limit or a
        // dropped connection (an iPad waking before Wi-Fi is back) must not.
        const refreshStatus = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
        if (refreshStatus === 400 || refreshStatus === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Remembered-device tokens, one per account signed in on this browser or phone.
const DEVICE_TOKENS_KEY = 'iron_forums_device_tokens';

function readDeviceTokens(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DEVICE_TOKENS_KEY) || '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export function deviceTokenFor(identifier: string): string {
  return readDeviceTokens()[identifier.trim().toLowerCase()] || '';
}

export function rememberDeviceToken(identifier: string, token: string) {
  const tokens = readDeviceTokens();
  tokens[identifier.trim().toLowerCase()] = token;
  localStorage.setItem(DEVICE_TOKENS_KEY, JSON.stringify(tokens));
}

// Auth API
export const authAPI = {
  login: async (username: string, password: string): Promise<AuthTokens | TwoFactorPrompt> => {
    const response = await api.post('token/', {
      username,
      password,
      device_token: deviceTokenFor(username),
    });
    return response.data;
  },

  verifyTwoFactor: async (challenge: string, code: string, rememberDevice: boolean): Promise<AuthTokens> => {
    const response = await api.post('users/two-factor/verify/', {
      challenge,
      code,
      remember_device: rememberDevice,
    });
    return response.data;
  },

  resendTwoFactor: async (challenge: string): Promise<{ challenge: string; email_hint: string }> => {
    const response = await api.post('users/two-factor/resend/', { challenge });
    return response.data;
  },

  twoFactorStatus: async (): Promise<TwoFactorStatus> => {
    const response = await api.get('users/two-factor/');
    return response.data;
  },

  twoFactorAction: async (
    action: 'start' | 'confirm' | 'disable' | 'forget_device' | 'forget_all',
    extra: Record<string, unknown> = {},
  ): Promise<TwoFactorStatus & { challenge?: string }> => {
    const response = await api.post('users/two-factor/', { action, ...extra });
    return response.data;
  },
  
  getMe: async (): Promise<User> => {
    const response = await api.get('users/me/');
    return response.data;
  },
  
  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.patch('users/update_profile/', data);
    return response.data;
  },

  requestAccountDeletion: async (): Promise<{
    success: boolean;
    message: string;
    deletion_requested_at: string;
    scheduled_purge_at: string;
    grace_days: number;
  }> => {
    const response = await api.post('users/request-deletion/');
    return response.data;
  },

  cancelAccountDeletion: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('users/cancel-deletion/');
    return response.data;
  },

  // Invite-based account setup (public — no auth header needed)
  verifySetupToken: async (token: string): Promise<{
    valid: boolean;
    first_name?: string;
    full_name?: string;
    email?: string;
    error?: string;
  }> => {
    const response = await api.get(`users/account-setup/${token}/`);
    return response.data;
  },

  completeSetup: async (token: string, password: string): Promise<AuthTokens & { user: User }> => {
    const response = await api.post(`users/account-setup/${token}/`, { password });
    return response.data;
  },

  requestPasswordReset: async (email: string): Promise<{
    success: boolean;
    message: string;
    reset_url?: string;
  }> => {
    const response = await api.post('users/password-reset/', { email });
    return response.data;
  },

  verifyPasswordResetToken: async (token: string): Promise<{
    valid: boolean;
    first_name?: string;
    email?: string;
    error?: string;
  }> => {
    const response = await api.get(`users/password-reset/${token}/`);
    return response.data;
  },

  confirmPasswordReset: async (token: string, password: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.post(`users/password-reset/${token}/`, { password });
    return response.data;
  },
};

// Snapshots API
export const snapshotsAPI = {
  getMySnapshots: async (): Promise<Snapshot[]> => {
    const response = await api.get('snapshots/my_snapshots/');
    return response.data;
  },
  
  getSnapshot: async (id: number): Promise<Snapshot> => {
    const response = await api.get(`snapshots/${id}/`);
    return response.data;
  },
  
  getCategories: async (): Promise<SnapshotCategory[]> => {
    const response = await api.get('snapshots/categories/');
    return response.data;
  },

  getBlankForm: async (
    snapshotType: string,
  ): Promise<{ snapshot_type: string; categories: SnapshotCategory[] }> => {
    const response = await api.get('snapshots/blank-form/', { params: { type: snapshotType } });
    return response.data;
  },
  
  getHistory: async (): Promise<SnapshotHistoryEntry[]> => {
    const response = await api.get('snapshots/history/');
    return response.data;
  },
  
  getAggregates: async (): Promise<SnapshotAggregates> => {
    const response = await api.get('snapshots/aggregates/');
    return response.data;
  },
  
  createSnapshot: async (data: Record<string, unknown>): Promise<Snapshot> => {
    const response = await api.post('snapshots/', data);
    return response.data;
  },
  
  updateSnapshot: async (id: number, data: Record<string, unknown>): Promise<Snapshot> => {
    const response = await api.patch(`snapshots/${id}/`, data);
    return response.data;
  },

  startDraft: async (startNew = false, month?: string): Promise<Snapshot> => {
    const response = await api.post('snapshots/start-draft/', {
      start_new: startNew,
      ...(month ? { month } : {}),
    });
    return response.data;
  },

  moveToCurrentMonth: async (id: number): Promise<Snapshot> => {
    const response = await api.post(`snapshots/${id}/move-to-current-month/`);
    return response.data;
  },

  reviseSnapshot: async (id: number, reason = ''): Promise<Snapshot> => {
    const response = await api.post(`snapshots/${id}/revise/`, { reason });
    return response.data;
  },

  deleteSnapshot: async (id: number, reason = ''): Promise<void> => {
    await api.delete(`snapshots/${id}/`, { data: { reason } });
  },
  
  getGroupSnapshots: async (): Promise<Snapshot[]> => {
    const response = await api.get('snapshots/');
    return response.data;
  },
  
  getGroupSnapshotsByGroup: async (groupId: number): Promise<Snapshot[]> => {
    const response = await api.get(`snapshots/group/${groupId}/`);
    return response.data;
  },

  getTrends: async (months: number = 6): Promise<MemberTrendsResponse> => {
    const response = await api.get(`snapshots/trends/?months=${months}`);
    return response.data;
  },

  getFacilitatorDashboard: async (month?: string): Promise<FacilitatorDashboardResponse> => {
    const query = month ? `?month=${month}` : '';
    const response = await api.get(`snapshots/facilitator-dashboard/${query}`);
    return response.data;
  },

  // Per-member, per-month history sheet for one forum (attendance, snapshot,
  // grow goals, captured payment status). start/end are YYYY-MM.
  getForumHistory: async (
    forumId: number,
    start: string,
    end: string,
  ): Promise<ForumHistoryResponse> => {
    const response = await api.get(
      `snapshots/facilitator-dashboard/history/?forum=${forumId}&start=${start}&end=${end}`,
    );
    return response.data;
  },

  // Same sheet as an Excel download (returns a Blob for a client-side save).
  exportForumHistoryXlsx: async (
    forumId: number,
    start: string,
    end: string,
  ): Promise<Blob> => {
    const response = await api.get(
      `snapshots/facilitator-dashboard/history/?forum=${forumId}&start=${start}&end=${end}&export=xlsx`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  getForumHealthRadar: async (
    forumId: number,
    month: string, // YYYY-MM
    metric: ForumHealthMetric,
    // When set, the radar shows this one member's own ratings instead of the
    // forum average (the "By member" view).
    memberId?: number,
    snapshotType?: string,
  ): Promise<ForumHealthRadarResponse> => {
    const memberQuery = memberId ? `&member_id=${memberId}` : '';
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(
      `snapshots/facilitator-dashboard/health-radar/?forum=${forumId}&month=${month}&metric=${metric}${memberQuery}${typeQuery}`,
    );
    return response.data;
  },

  getForumHealthTrend: async (
    forumId: number,
    metric: ForumHealthMetric,
    months: number = 6,
    snapshotType?: string,
  ): Promise<ForumHealthTrendResponse> => {
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(
      `snapshots/facilitator-dashboard/health-trend/?forum=${forumId}&metric=${metric}&months=${months}${typeQuery}`,
    );
    return response.data;
  },

  getMemberCategoryTrend: async (
    forumId: number,
    memberId: number,
    metric: ForumHealthMetric,
    months: number = 6,
  ): Promise<MemberCategoryTrendResponse> => {
    const response = await api.get(
      `snapshots/facilitator-dashboard/member-category-trend/?forum=${forumId}&member_id=${memberId}&metric=${metric}&months=${months}`,
    );
    return response.data;
  },

  getAdminActivity: async (
    months: number,
    forumId?: number,
    snapshotType?: string,
  ): Promise<AdminActivityResponse> => {
    const forumQuery = forumId ? `&forum=${forumId}` : '';
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(
      `snapshots/admin-dashboard/activity/?months=${months}${forumQuery}${typeQuery}`,
    );
    return response.data;
  },

  getOrgHealthRadar: async (
    month: string, // YYYY-MM
    metric: ForumHealthMetric,
    snapshotType?: string,
  ): Promise<OrgHealthRadarResponse> => {
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(
      `snapshots/admin-dashboard/health-radar/?month=${month}&metric=${metric}${typeQuery}`,
    );
    return response.data;
  },

  getOrgHealthTrend: async (
    metric: ForumHealthMetric,
    months: number = 6,
    snapshotType?: string,
  ): Promise<OrgHealthTrendResponse> => {
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(
      `snapshots/admin-dashboard/health-trend/?metric=${metric}&months=${months}${typeQuery}`,
    );
    return response.data;
  },

  getGroupThemes: async (groupId: number): Promise<{
    group?: { id: number; name: string };
    brief: string;
    stats_message?: string;
  }> => {
    const response = await api.get(`snapshots/group-themes/${groupId}/`);
    return response.data;
  },

  getAdminDashboard: async (
    months: number = 6,
    snapshotType?: string,
  ): Promise<AdminDashboardResponse> => {
    const typeQuery = snapshotType ? `&snapshot_type=${snapshotType}` : '';
    const response = await api.get(`snapshots/admin-dashboard/?months=${months}${typeQuery}`);
    return response.data;
  },

  getCurrentPeriod: async (month?: string): Promise<CurrentPeriodResponse> => {
    const response = await api.get(
      'snapshots/current-period/',
      month ? { params: { month } } : undefined,
    );
    return response.data;
  },

  updateHealthDefinitions: async (
    definitions: Pick<
      AreaHealthDefinition,
      'category' | 'healthy_looks_like' | 'positive_indicators' | 'slipping_signals'
    >[],
  ): Promise<AreaHealthDefinition[]> => {
    const response = await api.put('snapshots/health-definitions/', { definitions });
    return response.data;
  },
};

// Monthly snapshot groups (4-digit code, open to anyone, no per-month limit).
export const monthlyGroupsAPI = {
  // My current + past monthly groups.
  list: async (): Promise<MonthlySnapshotGroup[]> => {
    const response = await api.get('snapshots/monthly-groups/');
    return response.data;
  },

  getDetail: async (id: number): Promise<MonthlySnapshotGroupDetail> => {
    const response = await api.get(`snapshots/monthly-groups/${id}/`);
    return response.data;
  },

  // Start a group; returns the new group (with its 4-digit code).
  // The device timezone is the host clock the 2:00pm / 3-hour close uses.
  start: async (forumId?: number): Promise<MonthlySnapshotGroupDetail> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.post('snapshots/monthly-groups/', {
      ...(forumId ? { forum_id: forumId } : {}),
      ...(timezone ? { timezone } : {}),
    });
    return response.data;
  },

  join: async (code: string): Promise<MonthlySnapshotGroupDetail> => {
    const response = await api.post('snapshots/monthly-groups/join/', { code });
    return response.data;
  },

  leave: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`snapshots/monthly-groups/${id}/leave/`);
    return response.data;
  },

  removeMember: async (id: number, userId: number): Promise<MonthlySnapshotGroupDetail> => {
    const response = await api.post(`snapshots/monthly-groups/${id}/remove-member/`, { user_id: userId });
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`snapshots/monthly-groups/${id}/`);
  },

  // Facilitator oversight: every monthly group in forums the user facilitates.
  facilitator: async (): Promise<MonthlySnapshotGroup[]> => {
    const response = await api.get('snapshots/monthly-groups/facilitator/');
    return response.data;
  },
};

// Accountability Groups API (Phase 1) — cross-forum groups, propose/approve/
// accept invites, and per-item/per-group sharing.
export const accountabilityGroupsAPI = {
  list: async (): Promise<AccountabilityGroup[]> => {
    const response = await api.get('accountability/groups/');
    return response.data;
  },

  getDetail: async (id: number): Promise<AccountabilityGroupDetail> => {
    const response = await api.get(`accountability/groups/${id}/`);
    return response.data;
  },

  create: async (name: string, description?: string): Promise<AccountabilityGroupDetail> => {
    const response = await api.post('accountability/groups/', { name, description });
    return response.data;
  },

  leave: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`accountability/groups/${id}/leave/`);
    return response.data;
  },

  removeMember: async (id: number, userId: number): Promise<AccountabilityGroupDetail> => {
    const response = await api.post(`accountability/groups/${id}/remove-member/`, { user_id: userId });
    return response.data;
  },

  disband: async (id: number): Promise<void> => {
    await api.delete(`accountability/groups/${id}/`);
  },

  setShareSnapshot: async (id: number, enabled: boolean): Promise<AccountabilityGroupDetail> => {
    const response = await api.post(`accountability/groups/${id}/share-snapshot/`, { enabled });
    return response.data;
  },

  // Invitations
  propose: async (groupId: number, inviteeId: number): Promise<AccountabilityInvitation> => {
    const response = await api.post('accountability/invitations/', { group: groupId, invitee: inviteeId });
    return response.data;
  },

  listInvitations: async (): Promise<AccountabilityInvitation[]> => {
    const response = await api.get('accountability/invitations/');
    return response.data;
  },

  pendingInvitations: async (): Promise<AccountabilityPendingInvitations> => {
    const response = await api.get('accountability/invitations/pending/');
    return response.data;
  },

  approveInvitation: async (id: number): Promise<AccountabilityInvitation> => {
    const response = await api.post(`accountability/invitations/${id}/approve/`);
    return response.data;
  },

  acceptInvitation: async (id: number): Promise<AccountabilityGroupDetail> => {
    const response = await api.post(`accountability/invitations/${id}/accept/`);
    return response.data;
  },

  declineInvitation: async (id: number): Promise<AccountabilityInvitation> => {
    const response = await api.post(`accountability/invitations/${id}/decline/`);
    return response.data;
  },

  cancelInvitation: async (id: number): Promise<AccountabilityInvitation> => {
    const response = await api.post(`accountability/invitations/${id}/cancel/`);
    return response.data;
  },

  // Per-item, per-group sharing of a prayer / grow goal.
  getItemShares: async (itemType: 'prayer' | 'grow_goal', objectId: number): Promise<number[]> => {
    const response = await api.get('accountability/shares/', {
      params: { item_type: itemType, object_id: objectId },
    });
    return response.data.group_ids;
  },

  setItemShares: async (
    itemType: 'prayer' | 'grow_goal',
    objectId: number,
    groupIds: number[],
  ): Promise<number[]> => {
    const response = await api.post('accountability/shares/', {
      item_type: itemType,
      object_id: objectId,
      group_ids: groupIds,
    });
    return response.data.group_ids;
  },
};

// Commitments API
export const commitmentsAPI = {
  getAll: async (): Promise<Commitment[]> => {
    const response = await api.get('snapshots/commitments/');
    return response.data;
  },
  
  getMyActive: async (): Promise<Commitment[]> => {
    const response = await api.get('snapshots/commitments/my_active/');
    return response.data;
  },
  
  getForReview: async (): Promise<Commitment[]> => {
    const response = await api.get('snapshots/commitments/for_review/');
    return response.data;
  },
  
  getById: async (id: number): Promise<Commitment> => {
    const response = await api.get(`snapshots/commitments/${id}/`);
    return response.data;
  },
  
  create: async (data: Partial<Commitment>): Promise<Commitment> => {
    const response = await api.post('snapshots/commitments/', data);
    return response.data;
  },
  
  update: async (id: number, data: Partial<Commitment>): Promise<Commitment> => {
    const response = await api.patch(`snapshots/commitments/${id}/`, data);
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`snapshots/commitments/${id}/`);
  },
  
  complete: async (id: number): Promise<Commitment> => {
    const response = await api.post(`snapshots/commitments/${id}/complete/`);
    return response.data;
  },
  
  cancel: async (id: number): Promise<Commitment> => {
    const response = await api.post(`snapshots/commitments/${id}/cancel/`);
    return response.data;
  },
  
  uncomplete: async (id: number): Promise<Commitment> => {
    const response = await api.post(`snapshots/commitments/${id}/uncomplete/`);
    return response.data;
  },
  
  checkIn: async (id: number, completed: boolean, notes?: string, date?: string): Promise<CommitmentCheckIn> => {
    const response = await api.post(`snapshots/commitments/${id}/check_in/`, {
      completed,
      notes: notes || '',
      date: date || new Date().toISOString().split('T')[0]
    });
    return response.data;
  },
  
  uncheckIn: async (id: number, date?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`snapshots/commitments/${id}/uncheck_in/`, {
      date: date || new Date().toISOString().split('T')[0]
    });
    return response.data;
  },
};

// Grow-goal prompts: facilitator-initiated post-forum "set your grow goal"
// prompts, plus the member dashboard banner and per-member reminders.
export const growGoalPromptsAPI = {
  // Member: the open prompt the current user still needs to satisfy (or none).
  getActive: async (): Promise<ActiveGrowGoalPromptResponse> => {
    const response = await api.get('snapshots/grow-goal-prompts/active/');
    return response.data;
  },

  // Facilitator/admin: fire a grow-goal prompt to a forum's members.
  createForForum: async (forumId: number): Promise<GrowGoalPrompt> => {
    const response = await api.post('snapshots/grow-goal-prompts/', { forum: forumId });
    return response.data;
  },

  // Facilitator/admin: nudge one member who hasn't set a goal since the prompt.
  remindMember: async (forumId: number, memberId: number): Promise<{ success: boolean; pushed: boolean }> => {
    const response = await api.post('snapshots/grow-goal-prompts/remind-member/', {
      forum: forumId,
      member: memberId,
    });
    return response.data;
  },
};

// Facilitator/admin: lightweight per-member "attended this month's forum"
// checkbox tracking.
export const forumAttendanceAPI = {
  // The attendance map for a forum's month (defaults to the current month).
  get: async (forumId: number, month?: string): Promise<ForumAttendanceResponse> => {
    const query = month ? `&month=${month}` : '';
    const response = await api.get(`snapshots/forum-attendance/?forum=${forumId}${query}`);
    return response.data;
  },

  // Upsert one member's attendance for a forum's month (defaults current month).
  toggle: async (
    forumId: number,
    memberId: number,
    attended: boolean,
    month?: string,
  ): Promise<ForumAttendanceToggleResponse> => {
    const response = await api.post('snapshots/forum-attendance/toggle/', {
      forum: forumId,
      member: memberId,
      attended,
      ...(month ? { month } : {}),
    });
    return response.data;
  },
};

// Pre-snapshot data gate: admin-published profile requirements. Members see
// their unmet list (banner + snapshot gate) and submit custom-question answers;
// profile-field requirements are satisfied via the normal my-profile update.
export const profileRequirementsAPI = {
  // Member: active requirements the current user still needs to satisfy.
  getMyUnmet: async (): Promise<ProfileRequirement[]> => {
    const response = await api.get('users/profile-requirements/my-unmet/');
    return response.data;
  },

  // Member: answer (or re-answer) a custom question.
  submitAnswer: async (id: number, answerText: string): Promise<{ success: boolean }> => {
    const response = await api.post(`users/profile-requirements/${id}/answer/`, {
      answer_text: answerText,
    });
    return response.data;
  },

  // Admin: all requirements with completion counts.
  listAdmin: async (): Promise<ProfileRequirementAdmin[]> => {
    const response = await api.get('users/profile-requirements/');
    return response.data;
  },

  // Admin: publish a requirement (fans out a push to affected members).
  // `forum_id` scopes it to that forum's members; omit/null for org-wide.
  create: async (data: {
    kind: 'profile_field' | 'custom_question';
    field_name?: string;
    question_text?: string;
    forum_id?: number | null;
  }): Promise<ProfileRequirementAdmin> => {
    const response = await api.post('users/profile-requirements/', data);
    return response.data;
  },

  // Admin: retire a requirement (banner/gate stop referencing it).
  deactivate: async (id: number): Promise<ProfileRequirementAdmin> => {
    const response = await api.post(`users/profile-requirements/${id}/deactivate/`);
    return response.data;
  },

  // Admin: the whitelist of requirable profile fields.
  getFields: async (): Promise<{ value: string; label: string }[]> => {
    const response = await api.get('users/profile-requirements/fields/');
    return response.data;
  },

  // Admin: per-member responses behind a requirement's completion count
  // (answered members first, then everyone still outstanding).
  getAnswers: async (id: number): Promise<ProfileRequirementAnswerRow[]> => {
    const response = await api.get(`users/profile-requirements/${id}/answers/`);
    return response.data;
  },
};

// Messaging API
export const messagingAPI = {
  getThreads: async (): Promise<Thread[]> => {
    const response = await api.get('messaging/threads/');
    return response.data;
  },
  
  getMessages: async (threadId: number): Promise<Message[]> => {
    const response = await api.get(`messaging/threads/${threadId}/messages/`);
    return response.data;
  },
  
  sendToThread: async (threadId: number, content: string, images: File[] = []): Promise<Message> => {
    if (images.length === 0) {
      const response = await api.post(`messaging/threads/${threadId}/send/`, { content });
      return response.data;
    }
    const formData = new FormData();
    formData.append('content', content);
    images.forEach((image) => formData.append('images', image));
    const response = await api.post(`messaging/threads/${threadId}/send/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Toggle the current user's reaction on a message; returns the updated message.
  react: async (messageId: number, emoji: string): Promise<Message> => {
    const response = await api.post(`messaging/messages/${messageId}/react/`, { emoji });
    return response.data;
  },
  
  quickSend: async (recipientId: number, content: string): Promise<{ success: boolean; thread_id: number; recipient_name: string }> => {
    const response = await api.post('messaging/send/', {
      recipient_id: recipientId,
      content,
    });
    return response.data;
  },
  
  createThread: async (data: {
    name?: string;
    thread_type: string;
    kind?: string;
    forum?: number | null;
    accountability_group?: number | null;
    participant_ids?: number[];
  }): Promise<Thread> => {
    const response = await api.post('messaging/threads/', data);
    return response.data;
  },

  // Edit a message (sender only).
  updateMessage: async (messageId: number, content: string): Promise<Message> => {
    const response = await api.patch(`messaging/messages/${messageId}/`, { content });
    return response.data;
  },

  // Delete a message (sender only).
  deleteMessage: async (messageId: number): Promise<void> => {
    await api.delete(`messaging/messages/${messageId}/`);
  },

  // Add a participant to a group thread (forum-scope + cap enforced server-side).
  addParticipant: async (threadId: number, userId: number): Promise<Thread> => {
    const response = await api.post(`messaging/threads/${threadId}/add-participant/`, {
      user_id: userId,
    });
    return response.data;
  },

  // Mark a thread read (advances the last-read pointer).
  markRead: async (threadId: number): Promise<void> => {
    await api.post(`messaging/threads/${threadId}/read/`);
  },

  setMuted: async (threadId: number, muted: boolean): Promise<{ is_muted: boolean }> => {
    const response = await api.post(`messaging/threads/${threadId}/mute/`, { muted });
    return response.data;
  },

  removeThread: async (threadId: number): Promise<{ status: 'hidden' | 'left' }> => {
    const response = await api.post(`messaging/threads/${threadId}/remove/`);
    return response.data;
  },

  // Server-provided messaging config (caps), so the UI never hardcodes limits.
  getConfig: async (): Promise<{
    group_max_members: number;
    max_groups_per_member: number;
    is_cap_exempt: boolean;
    can_create_org_group: boolean;
  }> => {
    const response = await api.get('messaging/config/');
    return response.data;
  },
};

// Content API
export const contentAPI = {
  getTeachings: async (
    opts?: { visibility?: TeachingVisibility; manage?: boolean }
  ): Promise<Teaching[]> => {
    const params: Record<string, string> = {};
    if (opts?.visibility) params.visibility = opts.visibility;
    // The Knowledge Base admin page opts into managing "Jarvis knowledge only"
    // (hidden) items; the member Library omits this and only gets library docs.
    if (opts?.manage) params.manage = 'true';
    const response = await api.get('content/teachings/', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  
  getTeaching: async (id: number): Promise<Teaching> => {
    const response = await api.get(`content/teachings/${id}/`);
    return response.data;
  },
  
  createTeaching: async (data: TeachingCreate): Promise<Teaching> => {
    const response = await api.post('content/teachings/', data);
    return response.data;
  },
  
  updateTeaching: async (id: number, data: Partial<TeachingCreate>): Promise<Teaching> => {
    const response = await api.patch(`content/teachings/${id}/`, data);
    return response.data;
  },
  
  deleteTeaching: async (id: number): Promise<void> => {
    await api.delete(`content/teachings/${id}/`);
  },

  reorderTeachings: async (ids: number[]): Promise<void> => {
    await api.post('content/teachings/reorder/', { ids });
  },
  
  searchTeachings: async (query: string, visibility?: TeachingVisibility): Promise<Teaching[]> => {
    let url = `content/teachings/search/?q=${encodeURIComponent(query)}`;
    if (visibility) url += `&visibility=${encodeURIComponent(visibility)}`;
    const response = await api.get(url);
    return response.data;
  },
  
  semanticSearch: async (
    query: string, limit?: number, category?: string, visibility?: TeachingVisibility
  ): Promise<Teaching[]> => {
    let url = `content/teachings/semantic_search/?q=${encodeURIComponent(query)}`;
    if (limit) url += `&limit=${limit}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (visibility) url += `&visibility=${encodeURIComponent(visibility)}`;
    const response = await api.get(url);
    return response.data;
  },
  
  getByCategory: async (category: string): Promise<Teaching[]> => {
    const response = await api.get(`content/teachings/by_category/?category=${encodeURIComponent(category)}`);
    return response.data;
  },
  
  bulkUpload: async (teachings: TeachingCreate[]): Promise<BulkUploadResult> => {
    const response = await api.post('content/teachings/bulk_upload/', { teachings });
    return response.data;
  },
  
  embedAll: async (force?: boolean): Promise<{ success: number; failed: number; total: number }> => {
    const response = await api.post('content/teachings/embed_all/', { force });
    return response.data;
  },
  
  embedTeaching: async (id: number): Promise<{ status: string; id: number }> => {
    const response = await api.post(`content/teachings/${id}/embed/`);
    return response.data;
  },

  uploadPdf: async (
    file: File,
    opts?: {
      title?: string;
      author?: string;
      visibility?: string;
      scope_forum?: number | null;
      show_in_library?: boolean;
    }
  ): Promise<{ id: number; title: string; status: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.title) formData.append('title', opts.title);
    if (opts?.author) formData.append('author', opts.author);
    if (opts?.visibility) formData.append('visibility', opts.visibility);
    if (opts?.scope_forum != null) formData.append('scope_forum', String(opts.scope_forum));
    if (opts?.show_in_library != null) formData.append('show_in_library', String(opts.show_in_library));
    const response = await api.post('content/teachings/upload_pdf/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  ingestLink: async (payload: {
    url: string;
    visibility?: string;
    scope_forum?: number | null;
    title?: string;
    author?: string;
    categories?: string[];
    show_in_library?: boolean;
  }): Promise<Teaching> => {
    const response = await api.post('content/teachings/ingest_link/', payload);
    return response.data;
  },

  retryIngestion: async (id: number): Promise<Teaching> => {
    const response = await api.post(`content/teachings/${id}/retry/`);
    return response.data;
  },

  uploadAsset: async (
    id: number,
    role: 'content_sheet' | 'grow_guide' | 'image' | 'short_video' | 'full_video',
    opts: {
      file?: File;
      youtube_url?: string;
      is_hero?: boolean;
      sort_order?: number;
    },
    onProgress?: (fraction: number) => void,
  ): Promise<Teaching> => {
    const formData = new FormData();
    formData.append('role', role);
    if (opts.file) formData.append('file', opts.file);
    if (opts.youtube_url) formData.append('youtube_url', opts.youtube_url);
    if (opts.is_hero != null) formData.append('is_hero', String(opts.is_hero));
    if (opts.sort_order != null) formData.append('sort_order', String(opts.sort_order));
    const response = await api.post(
      `content/teachings/${id}/upload-asset/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Video files can be hundreds of MB; the global 60s timeout aborts
        // large uploads mid-flight, so give uploads 10 minutes.
        timeout: 10 * 60 * 1000,
        onUploadProgress: onProgress
          ? (event) => onProgress(event.total ? event.loaded / event.total : 0)
          : undefined,
      },
    );
    return response.data;
  },

  // Teaching media endpoints (short-video, full-video, image) stream or
  // 302-redirect to signed storage URLs, both of which require the JWT —
  // which bare <video>/<img> tags can't send. This resolves the underlying
  // playable URL with an authenticated request instead.
  resolveMediaUrl: async (mediaUrl: string): Promise<string> => {
    const response = await api.get<{ url: string }>(mediaUrl, { params: { json: 1 } });
    return response.data.url;
  },

  // AI-suggested category/keyword tags for the KB create/edit modal. Pass the
  // in-progress body text, or a teaching id to use an ingested transcript.
  // `existing` categories are deduped out server-side. Returns [] quietly when
  // no LLM key is configured.
  suggestKeywords: async (payload: {
    text?: string;
    teaching_id?: number;
    existing?: string[];
  }): Promise<{ suggestions: string[] }> => {
    const response = await api.post('content/teachings/suggest_keywords/', payload);
    return response.data;
  },

  // Permission-checked download of a teaching's content sheet or Grow Guide.
  // Local/dev streams the file. Production returns a signed storage URL as
  // JSON so we never follow a 302 with the JWT attached (that breaks R2).
  downloadTeachingAsset: async (
    id: number,
    role: 'content-sheet' | 'grow-guide',
    filename?: string,
  ): Promise<void> => {
    const response = await api.get(`content/teachings/${id}/${role}/`, {
      responseType: 'blob',
    });
    const contentType = String(response.headers['content-type'] || '');
    if (contentType.includes('application/json')) {
      const payload = JSON.parse(await (response.data as Blob).text()) as {
        url?: string;
        filename?: string;
      };
      if (!payload.url) {
        throw new Error('Download URL missing');
      }
      triggerBrowserDownload(payload.url, payload.filename || filename);
      return;
    }
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    triggerBrowserDownload(url, filename || 'teaching-download');
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },

  // Admin/library file URLs are auth-gated API paths, not public files.
  // Fetch with the JWT and trigger a download; leave http(s) media links alone.
  openAssetUrl: async (url: string, filename?: string): Promise<void> => {
    const path = toApiRelativePath(url);
    if (!path) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const response = await api.get(path, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || path.split('/').filter(Boolean).pop() || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  },

  fetchAssetObjectUrl: async (url: string): Promise<string> => {
    const path = toApiRelativePath(url);
    if (!path) return url;
    const response = await api.get(path, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data]));
  },

  downloadOriginalFile: async (id: number, filename?: string): Promise<void> => {
    return contentAPI.downloadTeachingAsset(id, 'content-sheet', filename || 'content-sheet');
  },
};

// Jarvis AI API
export const jarvisAPI = {
  chat: async (message: string, conversationId?: number, mode?: string): Promise<JarvisResponse> => {
    const response = await api.post('jarvis/chat/', {
      message,
      conversation_id: conversationId,
      ...(mode ? { mode } : {}),
    });
    return response.data;
  },
  
  getProbingQuestions: async (): Promise<{ questions: string[]; snapshot_month?: string }> => {
    const response = await api.get('jarvis/chat/');
    return response.data;
  },
  
  // Snapshot rating conversation. Pass a legacy `score` (v1) or
  // `state`/`direction` (Snapshot 2.0).
  chatAboutRating: async (
    categoryId: number,
    rating: { score?: number; state?: number; direction?: number },
    message?: string,
    conversationHistory?: ChatMessage[]
  ): Promise<SnapshotChatResponse> => {
    const response = await api.post('jarvis/snapshot-chat/', {
      category_id: categoryId,
      ...rating,
      message: message || '',
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },

  generateRatingSummary: async (
    categoryId: number,
    rating: { score?: number; state?: number; direction?: number },
    conversationHistory: ChatMessage[]
  ): Promise<SnapshotSummaryResponse> => {
    const response = await api.post('jarvis/snapshot-chat/', {
      category_id: categoryId,
      ...rating,
      conversation_history: conversationHistory,
      generate_summary: true,
    });
    return response.data;
  },

  chatAboutPulse: async (
    ownerExperience: string,
    message: string,
    conversationHistory: ChatMessage[],
  ): Promise<SnapshotChatResponse> => {
    const response = await api.post('jarvis/snapshot-chat/', {
      context_type: 'pulse',
      owner_experience: ownerExperience,
      message,
      conversation_history: conversationHistory,
    });
    return response.data;
  },

  generatePulseSummary: async (
    ownerExperience: string,
    conversationHistory: ChatMessage[],
  ): Promise<SnapshotSummaryResponse> => {
    const response = await api.post('jarvis/snapshot-chat/', {
      context_type: 'pulse',
      owner_experience: ownerExperience,
      conversation_history: conversationHistory,
      generate_summary: true,
    });
    return response.data;
  },
  
  // Conversation history management
  getConversations: async (): Promise<JarvisConversation[]> => {
    const response = await api.get('jarvis/conversations/');
    return response.data;
  },
  
  getConversation: async (id: number): Promise<JarvisConversation> => {
    const response = await api.get(`jarvis/conversations/${id}/`);
    return response.data;
  },
  
  deleteConversation: async (id: number): Promise<void> => {
    await api.delete(`jarvis/conversations/${id}/`);
  },
  
  updateConversationTitle: async (id: number, title: string): Promise<JarvisConversation> => {
    const response = await api.patch(`jarvis/conversations/${id}/`, { title });
    return response.data;
  },

  // Long-term memory: what Jarvis remembers about the member (Settings)
  getMemories: async (): Promise<JarvisMemory[]> => {
    const response = await api.get('jarvis/memories/');
    return response.data;
  },

  deleteMemory: async (id: number): Promise<void> => {
    await api.delete(`jarvis/memories/${id}/`);
  },
  
  // Execute a confirmed action
  executeAction: async (actionType: string, data: Record<string, unknown>): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await api.post('jarvis/action/', {
      action_type: actionType,
      ...data,
    });
    return response.data;
  },
  
  // Onboarding
  getOnboardingGreeting: async (): Promise<OnboardingGreeting> => {
    const response = await api.get('jarvis/onboarding/');
    return response.data;
  },
  
  sendOnboardingMessage: async (
    message: string,
    conversationHistory?: ChatMessage[]
  ): Promise<OnboardingResponse> => {
    const response = await api.post('jarvis/onboarding/', {
      message,
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },
  
  // Find message recipient
  getFindRecipientGreeting: async (): Promise<{ greeting: string }> => {
    const response = await api.get('jarvis/find-recipient/');
    return response.data;
  },
  
  findRecipient: async (
    message: string,
    conversationHistory?: ChatMessage[]
  ): Promise<{
    response: string;
    action?: { type: string; users?: UserListItem[]; query?: string; filters?: Record<string, string> };
    error?: boolean;
  }> => {
    const response = await api.post('jarvis/find-recipient/', {
      message,
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },
  
  // Commitment creation assistance
  getCommitmentCreationGreeting: async (): Promise<{ greeting: string }> => {
    const response = await api.get('jarvis/create-commitment/');
    return response.data;
  },
  
  createCommitmentChat: async (
    message: string,
    conversationHistory?: ChatMessage[]
  ): Promise<{
    response: string;
    action?: { 
      type: string; 
      commitment_id?: number;
      description?: string;
      frequency?: string;
      scheduling_mode?: string;
      target_count?: number;
      preferred_days?: number[];
      details?: string;
      preview?: {
        description: string;
        schedule_summary: string;
        share_label: string;
        frequency: string;
        scheduling_mode: string;
        target_count: number;
        preferred_days: number[];
        preferred_days_labels: string[];
      };
    };
    error?: boolean;
  }> => {
    const response = await api.post('jarvis/create-commitment/', {
      message,
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },
  
  // Find member (for Members page)
  getFindMemberGreeting: async (): Promise<{ greeting: string }> => {
    const response = await api.get('jarvis/find-member/');
    return response.data;
  },
  
  findMember: async (
    message: string,
    conversationHistory?: ChatMessage[]
  ): Promise<{
    response: string;
    action?: { type: string; users?: UserListItem[]; query?: string; filters?: Record<string, string> };
    error?: boolean;
  }> => {
    const response = await api.post('jarvis/find-member/', {
      message,
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },
  
  // Prayer creation assistance
  getPrayerCreationGreeting: async (): Promise<{ greeting: string }> => {
    const response = await api.get('jarvis/create-prayer/');
    return response.data;
  },
  
  createPrayerChat: async (
    message: string,
    conversationHistory?: ChatMessage[]
  ): Promise<{
    response: string;
    action?: { 
      type: string;
      prayer?: PrayerRequest;
    };
    error?: boolean;
  }> => {
    const response = await api.post('jarvis/create-prayer/', {
      message,
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },

  getAdminInsights: async (): Promise<AdminInsights> => {
    const response = await api.get('jarvis/admin-insights/');
    return response.data;
  },

  profileSuggest: async (field: string, currentValue?: string, context?: Record<string, unknown>): Promise<{ suggestion: string; field: string }> => {
    const response = await api.post('jarvis/profile-suggest/', { field, current_value: currentValue || '', context: context || {} });
    return response.data;
  },

  // Snapshot-style guided assist: assembles the member's own answers (not
  // canned text) into a profile paragraph.
  profileInterview: async (
    field: string,
    answers: { question: string; answer: string }[],
  ): Promise<{ draft: string; field: string }> => {
    const response = await api.post('jarvis/profile-interview/', { field, answers });
    return response.data;
  },

  commitmentSuggest: async (context: Record<string, unknown>): Promise<{ suggestions: GrowGoalSuggestion[] }> => {
    const response = await api.post('jarvis/commitment-suggest/', { context });
    return response.data;
  },

  // Conversational grow-goal coaching seeded with the just-completed snapshot.
  // Empty message returns Jarvis's opening weakness summary + goal directions.
  growGoalChat: async (
    context: Record<string, unknown>,
    message?: string,
    conversationHistory?: ChatMessage[]
  ): Promise<{ response: string; error?: boolean }> => {
    const response = await api.post('jarvis/grow-goal-chat/', {
      context,
      message: message || '',
      conversation_history: conversationHistory || [],
    });
    return response.data;
  },

  // Extract structured onboarding fields from pasted resume / LinkedIn text.
  // Returns only the fields the text supports — the member reviews and applies.
  extractFromResume: async (text: string): Promise<{ fields: ResumeExtractFields }> => {
    const response = await api.post('jarvis/resume-extract/', { text });
    return response.data;
  },
};

export interface ResumeExtractFields {
  bio?: string;
  title?: string;
  industry?: string;
  business_type?: string;
  years_in_business?: number;
  hobbies_interests?: string;
  areas_of_expertise?: string[];
}

export interface GrowGoalSuggestion {
  description: string;
  category_name: string;
  frequency: 'one_time' | 'daily' | 'weekly' | 'monthly';
}

// Users API
export const usersAPI = {
  getGroups: async (): Promise<SnapshotGroup[]> => {
    const response = await api.get('users/groups/');
    return response.data;
  },
  
  getAllGroups: async (): Promise<SnapshotGroupListItem[]> => {
    const response = await api.get('users/groups/all_groups/');
    return response.data;
  },
  
  getGroupMembers: async (groupId: number): Promise<User[]> => {
    const response = await api.get(`users/groups/${groupId}/members/`);
    return response.data;
  },
  
  removeGroupMember: async (groupId: number, userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/groups/${groupId}/remove-member/`, { user_id: userId });
    return response.data;
  },

  addGroupMember: async (groupId: number, userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/groups/${groupId}/add-member/`, { user_id: userId });
    return response.data;
  },

  // Edit a group's name and/or forum association. Pass forumId: null to
  // detach the group from any forum (standalone group).
  updateGroup: async (
    groupId: number,
    data: { name?: string; forum_id?: number | null },
  ): Promise<SnapshotGroup> => {
    const response = await api.patch(`users/groups/${groupId}/`, data);
    return response.data;
  },

  deleteGroup: async (groupId: number): Promise<void> => {
    await api.delete(`users/groups/${groupId}/`);
  },
  
  // Get all users for directory
  getDirectory: async (): Promise<UserListItem[]> => {
    const response = await api.get('users/directory/');
    // Be tolerant of either a plain array or a paginated {results} envelope so
    // a server-side pagination change can never white-screen the picker again.
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results ?? []);
  },
  
  // Invitations
  getMyInvitations: async (): Promise<GroupInvitation[]> => {
    const response = await api.get('users/invitations/my_invitations/');
    return response.data;
  },
  
  sendInvitation: async (groupId: number, data: { user_id?: number; email?: string; message?: string }): Promise<GroupInvitation> => {
    const response = await api.post(`users/groups/${groupId}/invite/`, data);
    return response.data;
  },
  
  acceptInvitation: async (id: number): Promise<{ success: boolean; message: string; group: SnapshotGroup }> => {
    const response = await api.post(`users/invitations/${id}/accept/`);
    return response.data;
  },
  
  declineInvitation: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/invitations/${id}/decline/`);
    return response.data;
  },
  
  verifyInviteToken: async (token: string): Promise<InviteTokenInfo> => {
    const response = await api.get(`users/invitations/verify/${token}/`);
    return response.data;
  },
  
  acceptInviteToken: async (token: string): Promise<{ success: boolean; message: string; group: SnapshotGroup }> => {
    const response = await api.post(`users/invitations/verify/${token}/`);
    return response.data;
  },
  
  getPendingInvitations: async (groupId: number): Promise<GroupInvitation[]> => {
    const response = await api.get(`users/groups/${groupId}/pending_invitations/`);
    return response.data;
  },
  
  createGroup: async (name: string, forumId?: number): Promise<SnapshotGroup> => {
    const response = await api.post('users/groups/', { name, ...(forumId && { forum_id: forumId }) });
    return response.data;
  },
  
  getGroupActivity: async (groupId: number): Promise<GroupActivity> => {
    const response = await api.get(`users/groups/${groupId}/activity/`);
    return response.data;
  },

  // Admin/facilitator: master member list with the full collected profile
  // data (includes the address/contact fields the directory hides). Admins
  // get everyone; facilitators are server-scoped to their own forums.
  getAdminMembers: async (params?: AdminMembersQuery): Promise<AdminMemberRow[]> => {
    const response = await api.get('users/admin/members/', { params });
    return response.data;
  },

  // Admin/facilitator: download the same (filtered, scoped) list as a CSV.
  exportAdminMembersCsv: async (params?: AdminMembersQuery): Promise<void> => {
    const response = await api.get('users/admin/members/export/', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `iron_forums_members_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Invite a brand-new member (admin/facilitator). Creates the account and
  // emails them a setup link to choose a password. `custom_monthly_amount`
  // (scholarship / negotiated rate) permanently overrides the forum/tier
  // price for this member everywhere billing is resolved — the invitee never
  // sees the forum's standard price or any other tier.
  inviteMember: async (data: {
    email: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    gender?: string;
    forum_id?: number | null;
    custom_monthly_amount?: string | null;
  }): Promise<{ success: boolean; message: string; user: UserListItem; setup_url?: string }> => {
    const response = await api.post('users/invite-member/', data);
    return response.data;
  },

  // Batch invites: several people at once, optionally scheduled for a future
  // send_at. Rows succeed/fail independently — check per-row results.
  inviteBatch: async (data: {
    invites: InviteRowPayload[];
    send_at?: string;
  }): Promise<BatchInviteResponse> => {
    const response = await api.post('users/invite-batch/', data);
    return response.data;
  },

  // Re-send the account-setup email for an invitee who never finished setup.
  resendInvite: async (
    userId: number,
    personalNote?: string,
  ): Promise<{
    success: boolean;
    message: string;
    setup_url?: string;
    invite_sent_at?: string | null;
    invite_sent_by_name?: string | null;
  }> => {
    const response = await api.post(`users/${userId}/resend-invite/`, {
      personal_note: personalNote ?? '',
    });
    return response.data;
  },

  // Still-pending scheduled invites (soonest first). The caller's own, or —
  // for admins with `all` — everyone's, for a cross-forum audit.
  getScheduledInvites: async (all = false): Promise<ScheduledInvite[]> => {
    const response = await api.get(`users/scheduled-invites/${all ? '?all=1' : ''}`);
    return response.data;
  },

  // Edit a pending scheduled invite (owner or admin). Any subset of fields.
  updateScheduledInvite: async (
    id: number,
    data: ScheduledInviteUpdate,
  ): Promise<ScheduledInvite> => {
    const response = await api.patch(`users/scheduled-invites/${id}/`, data);
    return response.data;
  },

  cancelScheduledInvite: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/scheduled-invites/${id}/cancel/`);
    return response.data;
  },

  bulkScheduledInvites: async (data: {
    ids: number[];
    action: 'reschedule' | 'cancel';
    send_at?: string;
  }): Promise<BulkScheduledInviteResponse> => {
    const response = await api.post('users/scheduled-invites/bulk/', data);
    return response.data;
  },

  // Admin: role, snapshot type, activation, dues, scholarship, pause,
  // and contact profile fields. Facilitators: profile / dues / pause /
  // access for members of forums they facilitate. Any subset of the
  // fields may be sent; returns the refreshed AdminMemberRow.
  // `stripe_warning` appears when a Stripe call failed.
  manageMember: async (
    userId: number,
    data: {
      role?: string;
      snapshot_type?: string;
      is_active?: boolean;
      scholarship?: boolean;
      paused?: boolean;
      pause_reason?: string;
      limit_app_access?: boolean;
      custom_monthly_amount?: string | null;
      first_name?: string;
      last_name?: string;
      email?: string;
      cell_phone?: string;
      location_city?: string;
      business_type?: string;
      guest_expires_at?: string;
    },
  ): Promise<AdminMemberRow & { stripe_warning?: string }> => {
    const response = await api.post(`users/${userId}/manage/`, data);
    return response.data;
  },

  purgeMember: async (userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/${userId}/purge/`);
    return response.data;
  },

  // Facilitator (own-forum members only) / admin (any): pause or resume a
  // member's paywall enforcement and, if they have one, their Stripe
  // subscription. `stripe_warning` is present when the app-side change
  // succeeded but the Stripe call itself failed.
  pauseMembership: async (
    forumId: number,
    memberId: number,
    paused: boolean,
    limitAppAccess?: boolean,
  ): Promise<{
    id: number;
    is_paused: boolean;
    limit_app_access: boolean;
    stripe_warning?: string;
  }> => {
    const payload: { paused: boolean; limit_app_access?: boolean } = { paused };
    if (paused) {
      payload.limit_app_access = Boolean(limitAppAccess);
    }
    const response = await api.post(
      `users/forums/${forumId}/members/${memberId}/pause/`,
      payload,
    );
    return response.data;
  },
};

// The admin members CSV export's field-picker checkboxes — mirrors
// AdminMemberSerializer / ADMIN_MEMBERS_CSV_COLUMNS on the backend.
export const ADMIN_MEMBER_EXPORT_FIELDS: AdminMemberExportField[] = [
  { key: 'full_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'cell_phone', label: 'Cell Phone' },
  { key: 'role', label: 'Role' },
  { key: 'forums', label: 'Forums' },
  { key: 'home_address', label: 'Home Address' },
  { key: 'home_city', label: 'Home City' },
  { key: 'home_state', label: 'Home State' },
  { key: 'home_zip', label: 'Home Zip' },
  { key: 'home_country', label: 'Home Country' },
  { key: 'title', label: 'Job Title' },
  { key: 'position', label: 'Position' },
  { key: 'industry', label: 'Industry' },
  { key: 'business_type', label: 'Company' },
  { key: 'website', label: 'Website' },
  { key: 'revenue_range', label: 'Revenue Range' },
  { key: 'growth_rate', label: 'Growth Rate' },
  { key: 'num_staff', label: 'Staff Count' },
  { key: 'business_address', label: 'Business Address' },
  { key: 'business_city', label: 'Business City' },
  { key: 'business_state', label: 'Business State' },
  { key: 'business_zip', label: 'Business Zip' },
  { key: 'business_country', label: 'Business Country' },
  { key: 'family_status', label: 'Family Status' },
  { key: 'has_children', label: 'Has Children' },
  { key: 'num_children', label: 'Number of Children' },
  { key: 'spouse_name', label: 'Spouse Name' },
  { key: 'church_attending', label: 'Church' },
  { key: 'membership_level', label: 'Membership Level' },
  { key: 'payment_status', label: 'Payment Status' },
  { key: 'monthly_price', label: 'Monthly Price' },
  { key: 'has_card', label: 'Card on file' },
  { key: 'last_paid', label: 'Last paid' },
  { key: 'next_charge', label: 'Next charge' },
  { key: 'is_paused', label: 'Paused' },
  { key: 'billing_note', label: 'Billing note' },
  { key: 'profile_completed', label: 'Profile Completed' },
  { key: 'date_joined', label: 'Date Joined' },
  { key: 'last_login', label: 'Last Login' },
];

// Notifications API
export interface NotificationPreferences {
  push_enabled: boolean;
  direct_messages: boolean;
  group_messages: boolean;
  commitment_reminders: boolean;
  snapshot_reminders: boolean;
  jarvis_reflections: boolean;
  meeting_requests: boolean;
  broadcasts?: boolean;
  updated_at?: string;
}

export const notificationsAPI = {
  registerDevice: async (token: string, platform: string): Promise<{ id: number }> => {
    const response = await api.post('notifications/register/', { token, platform });
    return response.data;
  },

  unregisterDevice: async (token: string): Promise<{ success: boolean }> => {
    const response = await api.post('notifications/unregister/', { token });
    return response.data;
  },

  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await api.get('notifications/preferences/');
    return response.data;
  },

  updatePreferences: async (data: Partial<NotificationPreferences>): Promise<NotificationPreferences> => {
    const response = await api.patch('notifications/preferences/', data);
    return response.data;
  },

  // Aggregate unread/pending counts for the nav badge dots (one cheap poll).
  getBadges: async (): Promise<{ messages: number; meetings: number; accountability: number; support?: number; total?: number }> => {
    const response = await api.get('notifications/badges/');
    return response.data;
  },

  listEvents: async (params?: { category?: string }): Promise<NotificationEventItem[]> => {
    const query = params?.category ? `?category=${encodeURIComponent(params.category)}` : '';
    const response = await api.get(`notifications/events/${query}`);
    return response.data;
  },

  markEventRead: async (id: number): Promise<NotificationEventItem> => {
    const response = await api.post(`notifications/events/${id}/mark-read/`);
    return response.data;
  },
};

export type NotificationEventItem = {
  id: number;
  category: string;
  event_type: string;
  title: string;
  body: string;
  url: string;
  data: Record<string, string>;
  read_at: string | null;
  created_at: string;
};

// Org/forum announcements (Home inbox + push). Admins target the whole
// organization or any forum; facilitators only a forum they facilitate.
export const broadcastsAPI = {
  send: async (data: {
    audience: BroadcastAudience;
    forums?: number[];
    people?: number[];
    roles?: string[];
    title: string;
    body: string;
    send_email?: boolean;
  }): Promise<Broadcast> => {
    const response = await api.post('notifications/broadcasts/', data);
    return response.data;
  },

  // Recent broadcasts scoped to the requester (admin sees all; facilitator
  // sees only what they themselves sent).
  listRecent: async (): Promise<Broadcast[]> => {
    const response = await api.get('notifications/broadcasts/');
    return response.data;
  },
};

// Forums API
export const forumsAPI = {
  // Get all forums
  getAll: async (params?: { forum_type?: ForumType; state?: string }): Promise<ForumListItem[]> => {
    const queryParams = new URLSearchParams();
    if (params?.forum_type) queryParams.append('forum_type', params.forum_type);
    if (params?.state) queryParams.append('state', params.state);
    const response = await api.get(`users/forums/${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
    return response.data;
  },
  
  // Get forum details
  getById: async (id: number): Promise<ForumDetail> => {
    const response = await api.get(`users/forums/${id}/`);
    return response.data;
  },
  
  // Get forums the current user belongs to
  getMyForums: async (): Promise<ForumListItem[]> => {
    const response = await api.get('users/forums/my-forums/');
    return response.data;
  },
  
  // Get forums the current user facilitates
  getFacilitating: async (): Promise<Forum[]> => {
    const response = await api.get('users/forums/facilitating/');
    return response.data;
  },
  
  // Create a new forum (admin only). At least one facilitator is required
  // (enforced server-side; a 400 with a facilitator_ids error is returned).
  create: async (data: {
    name: string;
    city: string;
    state: string;
    meeting_address?: string;
    forum_type: ForumType;
    location_name?: string;
    latitude?: string | null;
    longitude?: string | null;
    meeting_day?: MeetingDay | '';
    meeting_week?: MeetingWeek | '';
    meeting_start_time?: string | null;
    meeting_end_time?: string | null;
    monthly_price?: string;
    stripe_price_id?: string;
    advisor_monthly_price?: string;
    advisor_stripe_price_id?: string;
    facilitator_ids: number[];
  }): Promise<Forum> => {
    const response = await api.post('users/forums/', data);
    return response.data;
  },
  
  // Update a forum (admin any forum; facilitator only forums they facilitate).
  update: async (id: number, data: Partial<{
    name: string;
    city: string;
    state: string;
    meeting_address: string;
    forum_type: ForumType;
    location_name: string;
    latitude: string | null;
    longitude: string | null;
    meeting_day: MeetingDay | '';
    meeting_week: MeetingWeek | '';
    meeting_start_time: string | null;
    meeting_end_time: string | null;
    monthly_price: string;
    stripe_price_id: string;
    advisor_monthly_price: string;
    advisor_stripe_price_id: string;
    facilitator_ids: number[];
  }>): Promise<Forum> => {
    const response = await api.patch(`users/forums/${id}/`, data);
    return response.data;
  },

  // Upload/replace the forum's meeting-location photo (multipart). Returns the
  // refreshed forum (with location_photo set). Same storage backend as avatars.
  uploadPhoto: async (id: number, file: Blob, filename = 'forum-photo.jpg'): Promise<ForumDetail> => {
    const form = new FormData();
    form.append('file', file, filename);
    const response = await api.post(`users/forums/${id}/photo/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Address autocomplete via the backend OpenStreetMap Nominatim proxy.
  geocodeSearch: async (query: string): Promise<GeocodeSuggestion[]> => {
    const response = await api.get(`users/geocode/search/?q=${encodeURIComponent(query)}`);
    return response.data;
  },
  
  // Add member to forum
  addMember: async (forumId: number, userId: number): Promise<{ success: boolean; message: string; warning?: string }> => {
    const response = await api.post(`users/forums/${forumId}/add-member/`, { user_id: userId });
    return response.data;
  },
  
  // Remove member from forum
  removeMember: async (forumId: number, userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/forums/${forumId}/remove-member/`, { user_id: userId });
    return response.data;
  },
  
  // Add facilitator to forum (admin only)
  addFacilitator: async (forumId: number, userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/forums/${forumId}/add-facilitator/`, { user_id: userId });
    return response.data;
  },
  
  // Remove facilitator from forum (admin only)
  removeFacilitator: async (forumId: number, userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/forums/${forumId}/remove-facilitator/`, { user_id: userId });
    return response.data;
  },
  
  // Invite user to forum
  invite: async (forumId: number, data: { user_id?: number; email?: string; message?: string }): Promise<ForumInvitation & { warning?: string }> => {
    const response = await api.post(`users/forums/${forumId}/invite/`, data);
    return response.data;
  },
  
  // Get pending invitations for a forum
  getPendingInvitations: async (forumId: number): Promise<ForumInvitation[]> => {
    const response = await api.get(`users/forums/${forumId}/pending-invitations/`);
    return response.data;
  },
  
  // Get user's pending forum invitations
  getMyForumInvitations: async (): Promise<ForumInvitation[]> => {
    const response = await api.get('users/forum-invitations/my-invitations/');
    return response.data;
  },
  
  // Accept forum invitation
  acceptInvitation: async (invitationId: number): Promise<{ success: boolean; message: string; forum: Forum }> => {
    const response = await api.post(`users/forum-invitations/${invitationId}/accept/`);
    return response.data;
  },
  
  // Decline forum invitation
  declineInvitation: async (invitationId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`users/forum-invitations/${invitationId}/decline/`);
    return response.data;
  },

  // Facilitator: per-member dues/payment tracking for a forum.
  getPaymentTracking: async (forumId: number): Promise<{ forum_id: number; members: PaymentTrackingRow[] }> => {
    const response = await api.get(`users/forums/${forumId}/payment-tracking/`);
    return response.data;
  },

  sendPaymentReminder: async (forumId: number, memberId: number): Promise<PaymentReminderFields> => {
    const response = await api.post(`users/forums/${forumId}/members/${memberId}/payment-reminder/`);
    return response.data;
  },

  // Facilitator: consolidated detail (full snapshot history, grow goals,
  // shared prayers) for one member of a forum they facilitate.
  getMemberDetail: async (forumId: number, memberId: number): Promise<FacilitatorMemberDetail> => {
    const response = await api.get(`users/forums/${forumId}/member-detail/${memberId}/`);
    return response.data;
  },
};

// Private facilitator notes about members (facilitator/admin only).
export const facilitatorNotesAPI = {
  list: async (memberId?: number): Promise<FacilitatorNote[]> => {
    const q = memberId ? `?member=${memberId}` : '';
    const response = await api.get(`users/facilitator-notes/${q}`);
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results ?? []);
  },
  create: async (data: { member: number; forum?: number | null; content: string }): Promise<FacilitatorNote> => {
    const response = await api.post('users/facilitator-notes/', data);
    return response.data;
  },
  update: async (id: number, content: string): Promise<FacilitatorNote> => {
    const response = await api.patch(`users/facilitator-notes/${id}/`, { content });
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`users/facilitator-notes/${id}/`);
  },
};

// Events / Calendar API (Phase 1 — .ics add-to-calendar, no OAuth)
export const eventsAPI = {
  // Upcoming occurrences across the user's forums (default window 7 days).
  // Default is the caller's own forums. `mine` is an explicit alias; `all`
  // is the admin org-wide override.
  rsvpOccurrence: async (params: {
    eventId?: number | null;
    scheduleId?: number | null;
    date?: string | null;
    status: 'going' | 'not_going' | 'maybe';
  }): Promise<{ id: number; status: string }> => {
    const response = await api.post('events/occurrence-rsvp/', {
      event: params.eventId || undefined,
      schedule: params.scheduleId || undefined,
      date: params.date || undefined,
      status: params.status,
    });
    return response.data;
  },

  getOccurrenceRsvps: async (params: {
    eventId?: number | null;
    scheduleId?: number | null;
    date?: string | null;
  }): Promise<{ event: number; my_rsvp: string | null; rsvps: Record<string, string> }> => {
    const query = new URLSearchParams();
    if (params.eventId) query.set('event', String(params.eventId));
    if (params.scheduleId) query.set('schedule', String(params.scheduleId));
    if (params.date) query.set('date', params.date);
    const response = await api.get(`events/occurrence-rsvp/?${query.toString()}`);
    return response.data;
  },

  getUpcoming: async (params?: { days?: number; past_days?: number; forum?: number; mine?: boolean; all?: boolean }): Promise<UpcomingEvent[]> => {
    const query = new URLSearchParams();
    if (params?.days) query.append('days', String(params.days));
    if (params?.past_days) query.append('past_days', String(params.past_days));
    if (params?.forum) query.append('forum', String(params.forum));
    if (params?.mine) query.append('mine', '1');
    if (params?.all) query.append('all', '1');
    const response = await api.get(`events/upcoming/${query.toString() ? '?' + query.toString() : ''}`);
    return response.data;
  },

  // Forum roster (members + facilitators), privacy-redacted.
  getRoster: async (forumId: number): Promise<ForumRoster> => {
    const response = await api.get(`events/forums/${forumId}/roster/`);
    return response.data;
  },

  // Schedule CRUD (facilitator/admin only on write).
  getSchedules: async (forumId: number): Promise<ForumSchedule[]> => {
    const response = await api.get(`events/schedules/?forum=${forumId}`);
    return response.data;
  },

  createSchedule: async (data: Partial<ForumSchedule> & { forum_id: number }): Promise<ForumSchedule> => {
    const response = await api.post('events/schedules/', data);
    return response.data;
  },

  updateSchedule: async (id: number, data: Partial<ForumSchedule>): Promise<ForumSchedule> => {
    const response = await api.patch(`events/schedules/${id}/`, data);
    return response.data;
  },

  deleteSchedule: async (id: number): Promise<void> => {
    await api.delete(`events/schedules/${id}/`);
  },

  // Single-occurrence override (facilitator/admin): persist a concrete Event
  // that supersedes ONE computed occurrence of a recurring schedule — a
  // reschedule (status 'scheduled') or a skip (status 'cancelled') for that
  // date only. The recurring pattern itself is untouched.
  createEvent: async (data: {
    forum_id: number;
    schedule: number | null;
    is_override?: boolean;
    occurrence_date?: string | null;
    title: string;
    starts_at: string;
    ends_at: string;
    location?: string;
    description?: string;
    status?: 'scheduled' | 'cancelled';
  }): Promise<{ id: number }> => {
    const response = await api.post('events/events/', data);
    return response.data;
  },

  // Edit/cancel an already-persisted event (override or one-off).
  updateEvent: async (
    id: number,
    data: Partial<{
      title: string;
      starts_at: string;
      ends_at: string;
      location: string;
      description: string;
      status: 'scheduled' | 'cancelled';
    }>,
  ): Promise<{ id: number }> => {
    const response = await api.patch(`events/events/${id}/`, data);
    return response.data;
  },

  // Fetch an event's .ics via the authenticated client. On native, hand the
  // file to the OS share sheet so Calendar.app can add it. On web, download.
  downloadIcs: async (icsUrl: string, filename = 'event.ics'): Promise<'native' | 'web'> => {
    const response = await api.get(icsUrl, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/calendar' });
    if (Capacitor.isNativePlatform()) {
      const { openIcsOnDevice } = await import('../native/openIcs');
      await openIcsOnDevice(blob, filename);
      return 'native';
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return 'web';
  },
};

// Calendar connections (Phase 3): per-user Google/Outlook two-way sync. The
// OAuth flow mirrors LinkedIn (auth-url -> popup -> exchange); tokens live only
// on the server and are never returned here.
export const calendarAPI = {
  getConnections: async (): Promise<CalendarConnection[]> => {
    const response = await api.get('events/calendar/connections/');
    return response.data;
  },

  getAuthUrl: async (
    provider: CalendarProvider,
  ): Promise<{
    configured: boolean;
    authorization_url?: string;
    state?: string;
    redirect_uri?: string;
  }> => {
    const response = await api.get(`events/calendar/${provider}/auth-url/`);
    return response.data;
  },

  exchange: async (
    provider: CalendarProvider,
    code: string,
    redirectUri?: string,
  ): Promise<CalendarConnection> => {
    const response = await api.post(`events/calendar/${provider}/exchange/`, {
      code,
      redirect_uri: redirectUri,
    });
    return response.data;
  },

  disconnect: async (id: number): Promise<void> => {
    await api.delete(`events/calendar/connections/${id}/`);
  },
};

// Member-to-member meeting requests (Phase 5). A proposer offers candidate
// times; the invitee accepts (picking one) or declines. On accept the meeting
// lands on both calendars with an auto Meet/Teams link (proposer's provider).
export const meetingsAPI = {
  list: async (box?: 'incoming' | 'outgoing'): Promise<MeetingRequest[]> => {
    const response = await api.get(
      `events/meetings/${box ? `?box=${box}` : ''}`,
    );
    return response.data;
  },

  create: async (data: {
    invitee_id: number;
    title: string;
    note?: string;
    meeting_mode?: MeetingMode;
    location?: string;
    duration_minutes: number;
    proposed_times: string[];
  }): Promise<MeetingRequest> => {
    const response = await api.post('events/meetings/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: {
      title: string;
      note?: string;
      meeting_mode?: MeetingMode;
      location?: string;
      duration_minutes: number;
      proposed_times: string[];
    },
  ): Promise<MeetingRequest> => {
    const response = await api.post(`events/meetings/${id}/edit/`, data);
    return response.data;
  },

  accept: async (id: number, selectedTime?: string): Promise<MeetingRequest> => {
    const response = await api.post(`events/meetings/${id}/accept/`, {
      selected_time: selectedTime,
    });
    return response.data;
  },

  decline: async (id: number): Promise<MeetingRequest> => {
    const response = await api.post(`events/meetings/${id}/decline/`);
    return response.data;
  },

  hide: async (id: number): Promise<void> => {
    await api.post(`events/meetings/${id}/hide/`);
  },

  cancel: async (id: number, reason?: string): Promise<MeetingRequest> => {
    const response = await api.post(`events/meetings/${id}/cancel/`, {
      reason: reason || '',
    });
    return response.data;
  },

  // Poll for the conference link after accept (provisioned asynchronously).
  refreshConference: async (id: number): Promise<MeetingRequest> => {
    const response = await api.post(`events/meetings/${id}/refresh-conference/`);
    return response.data;
  },
};

// Support & Feedback: members submit problems/ideas/questions; admins triage.
// Five 10 MB screenshots on a phone connection can outlast the 60s default.
const SCREENSHOT_UPLOAD = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 3 * 60 * 1000,
};

export const feedbackAPI = {
  submit: async (kind: string, message: string, page?: string, screenshots: File[] = []): Promise<FeedbackItem> => {
    if (screenshots.length === 0) {
      const response = await api.post('users/feedback/', { kind, message, page: page || '' });
      return response.data;
    }
    const formData = new FormData();
    formData.append('kind', kind);
    formData.append('message', message);
    formData.append('page', page || '');
    screenshots.forEach((file) => formData.append('screenshots', file));
    const response = await api.post('users/feedback/', formData, SCREENSHOT_UPLOAD);
    return response.data;
  },

  // Members get their own submissions; admins get everyone's.
  getAll: async (status?: string): Promise<FeedbackItem[]> => {
    const response = await api.get('users/feedback/', { params: status ? { status } : {} });
    return response.data;
  },

  // Admin only: triage (new -> reviewed -> done).
  updateStatus: async (id: number, status: string): Promise<FeedbackItem> => {
    const response = await api.patch(`users/feedback/${id}/`, { status });
    return response.data;
  },
};

export const ticketsAPI = {
  list: async (params?: { closed?: boolean; archived?: boolean; q?: string; status?: string; priority?: string; kind?: string }): Promise<TicketSummary[]> => {
    const response = await api.get('users/tickets/', {
      params: {
        ...(params?.archived ? { archived: '1' } : {}),
        ...(params?.closed ? { closed: '1' } : {}),
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.priority ? { priority: params.priority } : {}),
        ...(params?.kind ? { kind: params.kind } : {}),
      },
    });
    return response.data;
  },
  get: async (id: number): Promise<TicketDetail> => {
    const response = await api.get(`users/tickets/${id}/`);
    return response.data;
  },
  update: async (id: number, payload: { status?: string; priority?: string; notify?: boolean }): Promise<TicketDetail> => {
    const response = await api.patch(`users/tickets/${id}/`, payload);
    return response.data;
  },
  staffChat: async (id: number, body: string, screenshots: File[] = [], shareWithDecider = false): Promise<TicketDetail> => {
    if (screenshots.length === 0) {
      const response = await api.post(`users/tickets/${id}/staff-chat/`, { body, share_with_decider: shareWithDecider });
      return response.data;
    }
    const formData = new FormData();
    formData.append('body', body);
    if (shareWithDecider) formData.append('share_with_decider', 'true');
    screenshots.forEach((file) => formData.append('screenshots', file));
    const response = await api.post(`users/tickets/${id}/staff-chat/`, formData, SCREENSHOT_UPLOAD);
    return response.data;
  },
  askAgent: async (id: number, question: string): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/ask-agent/`, { question });
    return response.data;
  },
  reply: async (id: number, body: string): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/reply/`, { body });
    return response.data;
  },
  close: async (
    id: number,
    note: string,
    outcome: 'shipped' | 'declined' = 'shipped',
    notify = false,
    whatsNew: { hidden?: boolean; note?: string } = {},
  ): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/close/`, {
      note, outcome, notify, whats_new_hidden: Boolean(whatsNew.hidden), whats_new_note: whatsNew.note || '',
    });
    return response.data;
  },
  emailPreview: async (
    id: number,
    payload: { action: 'status'; status: string } | { action: 'close'; outcome: 'shipped' | 'declined'; note: string },
  ): Promise<MemberEmailPreview> => {
    const response = await api.post(`users/tickets/${id}/email-preview/`, payload);
    return response.data;
  },
  reopen: async (id: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/reopen/`);
    return response.data;
  },
  archive: async (id: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/archive/`);
    return response.data;
  },
  unarchive: async (id: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/unarchive/`);
    return response.data;
  },
  detach: async (id: number, reportId: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/detach/`, { report_id: reportId });
    return response.data;
  },
  mine: async (): Promise<MyTicket[]> => {
    const response = await api.get('users/tickets/mine/');
    return response.data;
  },
  followUp: async (id: number, message: string, screenshots: File[] = []): Promise<TicketDetail> => {
    if (screenshots.length === 0) {
      const response = await api.post(`users/tickets/${id}/follow-up/`, { message });
      return response.data;
    }
    const formData = new FormData();
    formData.append('message', message);
    screenshots.forEach((file) => formData.append('screenshots', file));
    const response = await api.post(`users/tickets/${id}/follow-up/`, formData, SCREENSHOT_UPLOAD);
    return response.data;
  },
  imageObjectUrl: (url: string): Promise<string> => notesAPI.attachmentObjectUrl(url),
  requestCoding: async (id: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/coding-job/request/`);
    return response.data;
  },
  decideCoding: async (id: number, approve: boolean, reason = ''): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/coding-job/decide/`, { approve, reason });
    return response.data;
  },
  decidePlan: async (id: number, action: 'approve' | 'decline' | 'edit', text = ''): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/coding-job/plan/`, { action, text });
    return response.data;
  },
  decideBudget: async (id: number, keepGoing: boolean): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/coding-job/budget/`, { keep_going: keepGoing });
    return response.data;
  },
  cancelCoding: async (id: number): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/coding-job/cancel/`);
    return response.data;
  },
  codingApprovals: async (): Promise<CodingApproval[]> => {
    const response = await api.get('users/tickets/coding-approvals/');
    return response.data;
  },
  access: async (): Promise<TicketAccessPerson[]> => {
    const response = await api.get('users/tickets/access/');
    return response.data;
  },
  setAccess: async (userId: number, enabled: boolean): Promise<{ id: number; enabled: boolean }> => {
    const response = await api.post('users/tickets/access/', { user_id: userId, enabled });
    return response.data;
  },
  reorder: async (priority: string, ids: number[]): Promise<{ ids: number[] }> => {
    const response = await api.post('users/tickets/reorder/', { priority, ids });
    return response.data;
  },
  newCount: async (): Promise<number> => {
    const response = await api.get('users/tickets/new-count/');
    return response.data.count;
  },
  logForSomeone: async (payload: {
    userId: number; kind: string; message: string; via: string; screenshots?: File[]; notify: boolean;
  }): Promise<TicketDetail> => {
    const formData = new FormData();
    formData.append('user_id', String(payload.userId));
    formData.append('kind', payload.kind);
    formData.append('message', payload.message);
    formData.append('via', payload.via);
    formData.append('notify', payload.notify ? 'true' : 'false');
    (payload.screenshots || []).forEach((file) => formData.append('screenshots', file));
    const response = await api.post('users/tickets/log/', formData, SCREENSHOT_UPLOAD);
    return response.data;
  },
  logPreview: async (payload: { userId: number; kind: string; message: string; via: string }): Promise<MemberEmailPreview> => {
    const response = await api.post('users/tickets/log-preview/', {
      user_id: payload.userId, kind: payload.kind, message: payload.message, via: payload.via,
    });
    return response.data;
  },
  people: async (q: string): Promise<TicketPerson[]> => {
    const response = await api.get('users/tickets/people/', { params: { q } });
    return response.data;
  },
  decisionMakers: async (): Promise<TicketPerson[]> => {
    const response = await api.get('users/tickets/decision-makers/');
    return response.data;
  },
  setDecisionMaker: async (userId: number, enabled: boolean): Promise<TicketPerson & { enabled: boolean }> => {
    const response = await api.post('users/tickets/decision-makers/', { user_id: userId, enabled });
    return response.data;
  },
  requestDecision: async (id: number, deciderId: number, question: string): Promise<TicketDetail> => {
    const response = await api.post(`users/tickets/${id}/request-decision/`, { decider_id: deciderId, question });
    return response.data;
  },
  decisionLog: async (): Promise<TicketDecision[]> => {
    const response = await api.get('users/tickets/decision-log/');
    return response.data;
  },
  myDecisions: async (): Promise<DecisionTicket[]> => {
    const response = await api.get('users/tickets/decisions/');
    return response.data;
  },
  decisionComment: async (id: number, body: string): Promise<DecisionTicket> => {
    const response = await api.post(`users/tickets/${id}/decision-comment/`, { body });
    return response.data;
  },
  decide: async (id: number, decision: string): Promise<DecisionTicket> => {
    const response = await api.post(`users/tickets/${id}/decide/`, { decision });
    return response.data;
  },
};

export const whatsNewAPI = {
  list: async (): Promise<WhatsNewFeed> => {
    const response = await api.get('users/whats-new/');
    return response.data;
  },
  addEntry: async (title: string, body: string): Promise<WhatsNewItem> => {
    const response = await api.post('users/whats-new/entries/', { title, body });
    return response.data;
  },
  editEntry: async (id: number, title: string, body: string): Promise<WhatsNewItem> => {
    const response = await api.patch(`users/whats-new/entries/${id}/`, { title, body });
    return response.data;
  },
  deleteEntry: async (id: number): Promise<void> => {
    await api.delete(`users/whats-new/entries/${id}/`);
  },
  updateTicket: async (id: number, payload: { hidden?: boolean; title?: string; note?: string }): Promise<WhatsNewItem> => {
    const response = await api.patch(`users/whats-new/tickets/${id}/`, payload);
    return response.data;
  },
  buildDraft: async (): Promise<{ draft: WhatsNewDraft | null; reason?: string }> => {
    const response = await api.post('users/whats-new/digest/');
    return response.data;
  },
  approveDigest: async (id: number): Promise<{ digest: WhatsNewDigest }> => {
    const response = await api.post(`users/whats-new/digest/${id}/approve/`);
    return response.data;
  },
  discardDigest: async (id: number): Promise<{ digest: WhatsNewDigest }> => {
    const response = await api.post(`users/whats-new/digest/${id}/discard/`);
    return response.data;
  },
};

// Profiles API
export const profilesAPI = {
  // Get current user's full profile
  getMyProfile: async (): Promise<UserProfile> => {
    const response = await api.get('users/my-profile/');
    return response.data;
  },
  
  // Update current user's profile
  updateMyProfile: async (data: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await api.patch('users/my-profile/', data);
    return response.data;
  },
  
  // Get all member profiles for directory
  getAll: async (): Promise<UserProfileListItem[]> => {
    const response = await api.get('users/profiles/');
    return response.data;
  },
  
  // Get a specific user's profile
  getById: async (id: number): Promise<UserProfile> => {
    const response = await api.get(`users/${id}/profile/`);
    return response.data;
  },
  
  // Search profiles
  search: async (query: string, filters?: ProfileSearchFilters): Promise<UserProfileListItem[]> => {
    const response = await api.post('users/profiles/search/', { query, filters });
    return response.data;
  },

  // Get visibility settings
  getVisibility: async (): Promise<ProfileVisibilitySettings> => {
    const response = await api.get('users/my-visibility/');
    return response.data;
  },

  // Update visibility settings
  updateVisibility: async (data: Partial<ProfileVisibilitySettings>): Promise<ProfileVisibilitySettings> => {
    const response = await api.put('users/my-visibility/', data);
    return response.data;
  },

  // Membership registration commitments (tier + agreements + signature).
  getMyMembership: async (): Promise<MembershipRegistration> => {
    const response = await api.get('users/my-membership/');
    return response.data;
  },

  updateMyMembership: async (
    data: Partial<MembershipRegistration>,
  ): Promise<MembershipRegistration> => {
    const response = await api.patch('users/my-membership/', data);
    return response.data;
  },

  // Upload a cropped display photo. Pass the untouched original too so later
  // edits can zoom back out past the last crop.
  uploadAvatar: async (
    file: Blob,
    filename = 'avatar.png',
    original?: Blob | null,
  ): Promise<{ avatar_url: string }> => {
    const form = new FormData();
    form.append('file', file, filename);
    if (original) {
      const originalName = original.type === 'image/jpeg' ? 'source.jpg' : 'source.png';
      form.append('original', original, originalName);
    }
    const response = await api.post('users/my-avatar/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadAvatar: async (source = false): Promise<Blob> => {
    const response = await api.get('users/my-avatar/', {
      params: source ? { source: 1 } : undefined,
      responseType: 'blob',
    });
    return response.data;
  },
};

// LinkedIn "Connect" flow (OpenID Connect). Yields name/email/picture only —
// see backend/users/onboarding_views.py for the feasibility notes.
export const linkedinAPI = {
  getAuthUrl: async (flow: 'web' | 'native' = 'web'): Promise<{
    configured: boolean;
    authorization_url?: string;
    state?: string;
    redirect_uri?: string;
  }> => {
    const response = await api.get('users/linkedin/auth-url/', { params: { flow } });
    return response.data;
  },

  exchange: async (
    code: string,
    state: string,
  ): Promise<{
    first_name: string;
    last_name: string;
    email: string;
    email_verified: boolean;
    picture: string;
  }> => {
    const response = await api.post('users/linkedin/exchange/', {
      code,
      state,
    });
    return response.data;
  },

  getResult: async (state: string): Promise<
    | { status: 'pending' }
    | {
        status: 'success';
        profile: {
          first_name: string;
          last_name: string;
          email: string;
          email_verified: boolean;
          picture: string;
        };
      }
    | { status: 'error'; error: string }
  > => {
    const response = await api.get('users/linkedin/result/', { params: { state } });
    return response.data;
  },
};

export const expertiseTagsAPI = {
  getAll: async (params?: { search?: string; category?: string }): Promise<ExpertiseTag[]> => {
    const response = await api.get('users/expertise-tags/', { params });
    return response.data;
  },

  create: async (name: string, category?: string): Promise<ExpertiseTag> => {
    const response = await api.post('users/expertise-tags/', { name, category: category || 'skills' });
    return response.data;
  },
};

// Notes API (private member notes)
export const notesAPI = {
  list: async (query?: string): Promise<Note[]> => {
    const response = await api.get('notes/', { params: query ? { q: query } : undefined });
    return response.data;
  },

  create: async (data: NoteWrite): Promise<Note> => {
    const response = await api.post('notes/', data);
    return response.data;
  },

  update: async (id: number, data: NoteWrite): Promise<Note> => {
    const response = await api.patch(`notes/${id}/`, data);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`notes/${id}/`);
  },

  addAttachment: async (id: number, file: File): Promise<Note> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`notes/${id}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  removeAttachment: async (id: number, attachmentId: number): Promise<Note> => {
    const response = await api.delete(`notes/${id}/attachments/${attachmentId}/`);
    return response.data;
  },

  // Fetches the auth-gated attachment endpoint; returns a blob object URL
  // locally, or the signed storage URL when the API responds with JSON (R2).
  attachmentObjectUrl: async (attachmentUrl: string): Promise<string> => {
    const path = toApiRelativePath(attachmentUrl);
    if (!path) return attachmentUrl;
    const response = await api.get(path, { responseType: 'blob' });
    const contentType = String(response.headers['content-type'] || '');
    if (contentType.includes('application/json')) {
      const payload = JSON.parse(await (response.data as Blob).text()) as { url?: string };
      return payload.url || attachmentUrl;
    }
    // Keep the content type on the blob — without it the browser treats the
    // object URL as opaque bytes (renders garbage or force-downloads).
    return window.URL.createObjectURL(
      new Blob([response.data], { type: contentType || 'application/octet-stream' }),
    );
  },

  linkableMeetings: async (): Promise<LinkableMeetings> => {
    const response = await api.get('notes/linkable-meetings/');
    return response.data;
  },
};

// Prayers API
export const prayersAPI = {
  // Get all prayers visible to the user (own + group public)
  getAll: async (): Promise<PrayerRequest[]> => {
    const response = await api.get('prayers/');
    return response.data;
  },
  
  // Get all active prayers for praying (unified list)
  getAllActive: async (): Promise<UnifiedPrayer[]> => {
    const response = await api.get('prayers/all_active/');
    return response.data;
  },
  
  // Get only user's own prayers
  getMyPrayers: async (): Promise<PrayerRequest[]> => {
    const response = await api.get('prayers/my_prayers/');
    return response.data;
  },
  
  // Get group members' public prayers
  getGroupPrayers: async (): Promise<PrayerRequest[]> => {
    const response = await api.get('prayers/group_prayers/');
    return response.data;
  },
  
  // Get answered prayers
  getAnswered: async (): Promise<PrayerRequest[]> => {
    const response = await api.get('prayers/answered/');
    return response.data;
  },
  
  // Create a new prayer request
  create: async (data: { 
    title: string; 
    content: string; 
    is_private: boolean;
    share_with_facilitator?: boolean;
    shared_group_ids?: number[];
    praying_for_user_id?: number;
    praying_for_name?: string;
  }): Promise<PrayerRequest> => {
    const response = await api.post('prayers/', data);
    return response.data;
  },
  
  // Update a prayer request
  update: async (id: number, data: Partial<{ title: string; content: string; is_private: boolean; share_with_facilitator: boolean; shared_group_ids: number[]; is_answered: boolean; answered_note: string; praying_for_user_id?: number | null; praying_for_name?: string }>): Promise<PrayerRequest> => {
    const response = await api.patch(`prayers/${id}/`, data);
    return response.data;
  },
  
  // Delete a prayer request
  delete: async (id: number): Promise<void> => {
    await api.delete(`prayers/${id}/`);
  },
  
  // Mark a prayer as answered
  markAnswered: async (id: number, note?: string): Promise<PrayerRequest> => {
    const response = await api.post(`prayers/${id}/mark_answered/`, { note: note || '' });
    return response.data;
  },
  
  // Mark a prayer as unanswered (undo answered status)
  markUnanswered: async (id: number): Promise<PrayerRequest> => {
    const response = await api.post(`prayers/${id}/mark_unanswered/`);
    return response.data;
  },
  
  // Mark a snapshot prayer as answered
  markSnapshotAnswered: async (snapshotId: number, note?: string): Promise<PrayerRequest> => {
    const response = await api.post('prayers/mark_snapshot_answered/', { 
      snapshot_id: snapshotId, 
      note: note || '' 
    });
    return response.data;
  },
  
  // Get prayers for a specific group
  getGroupPrayersByGroup: async (groupId: number): Promise<{ prayers: PrayerRequest[]; snapshot_prayers: SnapshotPrayer[] }> => {
    const response = await api.get(`prayers/group/${groupId}/`);
    return response.data;
  },
  
  // Get structured prayer summary (fast, no AI call)
  getStructuredSummary: async (): Promise<StructuredPrayerSummary> => {
    const response = await api.get('prayers/structured_summary/');
    return response.data;
  },

  // Journal entries
  getJournalEntries: async (prayerId: number): Promise<PrayerJournalEntry[]> => {
    const response = await api.get(`prayers/${prayerId}/journal/`);
    return response.data;
  },

  addJournalEntry: async (prayerId: number, content: string, isPrivate?: boolean): Promise<PrayerJournalEntry> => {
    const response = await api.post(`prayers/${prayerId}/journal/`, {
      content,
      is_private: isPrivate,
    });
    return response.data;
  },

  deleteJournalEntry: async (prayerId: number, entryId: number): Promise<void> => {
    await api.delete(`prayers/${prayerId}/journal/${entryId}/`);
  },
};

// App metadata (native update nudge)
export const metaAPI = {
  getAppVersion: async (): Promise<AppVersionInfo> => {
    const response = await api.get('app-version/');
    return response.data;
  },
};

// Payments API (Stripe-hosted Checkout + subscription paywall)
export const paymentsAPI = {
  // Server-side payment snapshot. On success return, sessionId is validated
  // against Stripe while signed webhooks remain authoritative for activation.
  getStatus: async (sessionId?: string): Promise<PaymentStatusResponse> => {
    const response = await api.get('payments/status/', {
      params: sessionId ? { session_id: sessionId } : undefined,
    });
    return response.data;
  },

  // Start hosted Checkout using the authenticated user's server-side Forum price.
  createCheckoutSession: async (coverProcessingFee = true): Promise<{
    url?: string;
    session_id?: string;
    configured?: boolean;
    reason?: string;
    error?: string;
  }> => {
    const response = await api.post('payments/create-checkout-session/', {
      cover_processing_fee: coverProcessingFee,
    });
    return response.data;
  },

  setProcessingFee: async (coverProcessingFee: boolean): Promise<PaymentStatusResponse> => {
    const response = await api.post('payments/processing-fee/', {
      cover_processing_fee: coverProcessingFee,
    });
    return response.data;
  },

  // Member-facing billing card data (plan, status, renewal, default card).
  getBillingSummary: async (): Promise<BillingSummary> => {
    const response = await api.get('payments/summary/');
    return response.data;
  },

  // The member's own paid charges, each with its Stripe receipt link.
  getReceipts: async (): Promise<{ receipts: MemberReceipt[]; incomplete: boolean }> => {
    const response = await api.get('payments/receipts/');
    return response.data;
  },

  // Open Stripe's hosted Billing Portal; returns the URL to redirect to.
  createPortalSession: async (
    flow?: 'payment_method_update',
  ): Promise<{
    url?: string;
    configured?: boolean;
    reason?: string;
    error?: string;
  }> => {
    const response = await api.post(
      'payments/create-portal-session/',
      flow ? { flow } : {},
    );
    return response.data;
  },

  // Org-wide monthly billing day (admin). All dues go out on this day of the
  // month; null means each member renews on their signup anniversary.
  getBillingConfig: async (): Promise<{
    billing_day: number | null;
    next_payment_date: string | null;
  }> => {
    const response = await api.get('payments/billing-config/');
    return response.data;
  },

  setBillingConfig: async (
    billingDay: number | null,
  ): Promise<{
    billing_day: number | null;
    next_payment_date: string | null;
    aligned_count: number;
  }> => {
    const response = await api.put('payments/billing-config/', {
      billing_day: billingDay,
    });
    return response.data;
  },

  // The admin Billing table: one row per dues-paying member.
  getBillingOverview: async (): Promise<{ members: BillingOverviewRow[] }> => {
    const response = await api.get('payments/admin/billing-overview/');
    return response.data;
  },

  // Per-member billing controls (admin, in the Manage member modal).
  getMemberBilling: async (memberId: number): Promise<MemberBillingInfo> => {
    const response = await api.get(`payments/members/${memberId}/billing/`);
    return response.data;
  },

  setMemberNextChargeDate: async (
    memberId: number,
    date: string,
  ): Promise<MemberBillingInfo> => {
    const response = await api.post(
      `payments/members/${memberId}/next-charge-date/`,
      { date },
    );
    return response.data;
  },

  setMemberProcessingFee: async (
    memberId: number,
    cover: boolean,
  ): Promise<MemberBillingInfo> => {
    const response = await api.post(
      `payments/members/${memberId}/processing-fee/`,
      { cover_processing_fee: cover },
    );
    return response.data;
  },

  // Skip the member's next N monthly renewals (billing resumes automatically).
  skipMemberBilling: async (
    memberId: number,
    months: number,
    reason: string,
  ): Promise<MemberBillingInfo> => {
    const response = await api.post(
      `payments/members/${memberId}/skip-billing/`,
      { months, reason },
    );
    return response.data;
  },

  clearMemberBillingSkip: async (memberId: number): Promise<MemberBillingInfo> => {
    const response = await api.delete(`payments/members/${memberId}/skip-billing/`);
    return response.data;
  },

  sendUpdateCardEmail: async (memberId: number): Promise<{ sent: boolean }> => {
    const response = await api.post(`payments/members/${memberId}/update-card-email/`);
    return response.data;
  },

  scheduleMemberCharge: async (
    memberId: number,
    payload: { amount: string; charge_on: string; description?: string },
  ): Promise<ScheduledCharge> => {
    const response = await api.post(
      `payments/members/${memberId}/scheduled-charges/`,
      payload,
    );
    return response.data;
  },

  cancelScheduledCharge: async (chargeId: number): Promise<ScheduledCharge> => {
    const response = await api.delete(`payments/scheduled-charges/${chargeId}/`);
    return response.data;
  },
};

// ---- Prompt Versioning (admin) ----

export type EmailBlock =
  | { type: 'paragraph' | 'note'; text: string }
  | { type: 'callout'; text: string; tone: 'info' | 'warning' | 'success' }
  | { type: 'list'; items: string[]; ordered: boolean }
  | { type: 'button'; label: string; link: string }
  | { type: 'personal_note' | 'body' };

export interface EmailContent {
  subject: string;
  heading: string;
  greeting: string;
  blocks: EmailBlock[];
}

export interface EmailTemplateSummary {
  key: string;
  name: string;
  group: string;
  description: string;
  subject: string;
  customized: boolean;
  updated_at: string | null;
}

export interface EmailTemplateVersionInfo {
  id: number;
  version: number;
  is_active: boolean;
  notes: string;
  created_at: string;
  created_by: string;
  content: EmailContent;
}

export interface EmailTemplateDetail {
  key: string;
  name: string;
  group: string;
  description: string;
  variables: { name: string; description: string; sample: string }[];
  links: { name: string; label: string; sample: string }[];
  content: EmailContent;
  default_content: EmailContent;
  customized: boolean;
  versions: EmailTemplateVersionInfo[];
}

export const emailTemplatesAPI = {
  list: async (): Promise<EmailTemplateSummary[]> => (await api.get('/admin/email-templates/')).data,
  get: async (key: string): Promise<EmailTemplateDetail> => (await api.get(`/admin/email-templates/${key}/`)).data,
  save: async (key: string, content: EmailContent, notes: string) =>
    (await api.post(`/admin/email-templates/${key}/`, { content, notes })).data,
  preview: async (key: string, content: EmailContent): Promise<{ subject: string; html: string; text: string }> =>
    (await api.post(`/admin/email-templates/${key}/preview/`, { content })).data,
  sendTest: async (key: string, content: EmailContent): Promise<{ sent_to: string }> =>
    (await api.post(`/admin/email-templates/${key}/send-test/`, { content })).data,
  reset: async (key: string) => (await api.post(`/admin/email-templates/${key}/reset/`)).data,
  restore: async (key: string, versionId: number) =>
    (await api.post(`/admin/email-templates/${key}/versions/${versionId}/restore/`)).data,
};

export const promptsAPI = {
  list: async () => {
    const response = await api.get('/admin/prompts/');
    return response.data;
  },

  get: async (slug: string) => {
    const response = await api.get(`/admin/prompts/${slug}/`);
    return response.data;
  },

  createVersion: async (slug: string, data: { content: string; notes?: string; waffle_flag?: string; activate?: boolean }) => {
    const response = await api.post(`/admin/prompts/${slug}/versions/`, data);
    return response.data;
  },

  activateVersion: async (slug: string, versionId: number) => {
    const response = await api.post(`/admin/prompts/${slug}/versions/${versionId}/activate/`);
    return response.data;
  },

  getVersion: async (slug: string, versionId: number) => {
    const response = await api.get(`/admin/prompts/${slug}/versions/${versionId}/`);
    return response.data;
  },
};

// Monthly app sponsors: the member-dashboard banner reads the current month's
// sponsor; create/update/delete + logo upload are admin-only.
export const sponsorsAPI = {
  getCurrent: async (): Promise<CurrentSponsorResponse> => {
    const response = await api.get('sponsors/current/');
    return response.data;
  },

  list: async (): Promise<Sponsor[]> => {
    const response = await api.get('sponsors/');
    return response.data;
  },

  create: async (data: { name: string; month: string | null; link_url?: string; message?: string }): Promise<Sponsor> => {
    const response = await api.post('sponsors/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<{ name: string; month: string | null; link_url: string; message: string }>): Promise<Sponsor> => {
    const response = await api.patch(`sponsors/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`sponsors/${id}/`);
  },

  // Upload/replace the sponsor's logo (multipart). Returns the refreshed
  // sponsor. Same storage backend as avatars / forum location photos.
  uploadLogo: async (id: number, file: Blob, filename = 'logo.png'): Promise<Sponsor> => {
    const form = new FormData();
    form.append('file', file, filename);
    const response = await api.post(`sponsors/${id}/logo/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default api;
